'use client'

import { useEffect, useState } from 'react'

/**
 * True mesh gradient panel background: a base tone with six irregular,
 * heavily blurred radial blobs — every position, radius, rotation, and hue
 * drawn from a PRNG, so nothing tiles and no two renders repeat. Hues are
 * built from one random base hue plus analogous and counterpoint offsets,
 * which keeps each roll harmonious instead of clown-random.
 *
 * Theme-aware: the same geometry roll is rendered twice — a pastel field
 * (light base, soft blobs) shown in light mode and a deep field shown in
 * dark mode — toggled purely by CSS classes, so theme flips are instant
 * and no theme state is read in JS. Pair the panel's copy with the
 * `foreground` token so text flips with it.
 *
 * The server and first client paint use a fixed seed (hydration-safe); the
 * client re-rolls once after mount, so the mesh is different on every
 * visit. `seedKey` separates instances on the same page.
 */

function mulberry32(a: number) {
  return () => {
    a |= 0
    a = (a + 0x6d2b79f5) | 0
    let t = Math.imul(a ^ (a >>> 15), 1 | a)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

function hashKey(s: string) {
  let h = 7
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0
  return h
}

interface Blob {
  cx: number
  cy: number
  rx: number
  ry: number
  rot: number
  hue: number
  /** 0..1 — mapped to the theme's saturation band at render time */
  satT: number
  /** 0..1 — mapped to the theme's lightness band at render time */
  tone: number
  alpha: number
}

function Field({
  id,
  base,
  blobs,
  light,
  sat,
}: {
  id: string
  base: string
  blobs: Blob[]
  /** [min, span] lightness band for this theme */
  light: [number, number]
  /** [min, span] saturation band for this theme */
  sat: [number, number]
}) {
  return (
    <div className="absolute inset-0" style={{ background: base }}>
      <svg
        className="absolute inset-0 h-full w-full"
        viewBox="0 0 100 60"
        preserveAspectRatio="none"
      >
        <defs>
          {/* Heavy blur relative to the 100×60 viewBox: on short, wide
              panels the stretch would otherwise leave individual ellipses
              readable instead of one continuous field. */}
          <filter id={id} x="-80%" y="-80%" width="260%" height="260%">
            <feGaussianBlur stdDeviation="14" />
          </filter>
        </defs>
        <g filter={`url(#${id})`}>
          {blobs.map((b, i) => (
            <ellipse
              key={i}
              cx={b.cx}
              cy={b.cy}
              rx={b.rx}
              ry={b.ry}
              transform={`rotate(${b.rot} ${b.cx} ${b.cy})`}
              fill={`hsl(${Math.round(b.hue)} ${Math.round(sat[0] + b.satT * sat[1])}% ${Math.round(light[0] + b.tone * light[1])}%)`}
              fillOpacity={b.alpha}
            />
          ))}
        </g>
      </svg>
    </div>
  )
}

export function MeshPanel({
  seedKey = '',
  soft = false,
}: {
  seedKey?: string
  /** Quieter field for small cards: fewer, fainter blobs. */
  soft?: boolean
}) {
  const [seed, setSeed] = useState(1)
  useEffect(() => {
    setSeed(1 + Math.floor(Math.random() * 0xffff))
  }, [])

  const rand = mulberry32(seed * 131 + hashKey(seedKey))

  // One random base hue; companions sit analogous (±25–65°) with one
  // counterpoint across the wheel (150–210°) for tension.
  const baseHue = rand() * 360
  const hues = [
    baseHue,
    baseHue + 25 + rand() * 40,
    baseHue - (25 + rand() * 40),
    baseHue + 150 + rand() * 60,
    baseHue + 15 + rand() * 30,
  ].map((h) => ((h % 360) + 360) % 360)

  const blobs: Blob[] = Array.from({ length: soft ? 4 : 6 }, (_, i) => ({
    cx: rand() * 100,
    cy: rand() * 60,
    rx: 28 + rand() * 30,
    ry: 14 + rand() * 22,
    rot: rand() * 180,
    hue: hues[i % hues.length]!,
    satT: rand(),
    tone: rand(),
    alpha: soft ? 0.3 + rand() * 0.2 : 0.5 + rand() * 0.25,
  }))

  const h = Math.round(baseHue)
  // Filter ids must be valid CSS identifiers — seedKeys carry arbitrary
  // text (spaces, ampersands), and an invalid id makes url(#…) silently
  // fail, which renders the ellipses razor-sharp instead of blurred.
  const uid = (seedKey || 'p').replace(/[^a-zA-Z0-9_-]/g, '-')

  return (
    <div aria-hidden className="pointer-events-none absolute inset-0">
      {/* Light theme: pastel field, dark copy sits on it */}
      <div className="absolute inset-0 dark:hidden">
        <Field
          id={`mesh-l-${uid}`}
          base={`hsl(${h} 42% 93%)`}
          blobs={blobs}
          light={[70, 14]}
          sat={[55, 20]}
        />
        <div className="absolute inset-0 bg-white/10" />
      </div>
      {/* Dark theme: same geometry, tempered color — hues clearly present
          but held to a deep, muted band. Full pastel saturation at dark
          lightness reads as stage lighting; near-zero reads washed out. */}
      <div className="absolute inset-0 hidden dark:block">
        <Field
          id={`mesh-d-${uid}`}
          base={`hsl(${h} 30% 11%)`}
          blobs={blobs}
          light={[24, 11]}
          sat={[30, 16]}
        />
        <div className="absolute inset-0 bg-black/15" />
      </div>
    </div>
  )
}

export default MeshPanel
